/**
 * 전체팝업 (앱 접속 시 노출) — 일반 공지 팝업 · 약관 개정 팝업.
 *
 * 노출 정책 — 로그인당 1회. 억제는 '다시 보지 않기'(서버 기억).
 *   1) 언제 띄울지는 이 모듈이 정하지 않는다 — 모바일 부트스트랩(Sites/Mobile/Bootstrap)의
 *      '로그인 직후 묶음'이 loginKey 로 게이팅해 로그인당 한 번만 start() 를 부른다.
 *      (환경설정 동기화·위치 게이트와 같은 자리·같은 방식)
 *   2) G5.popupStamp 기준으로 팝업 데이터를 sessionStorage 에 캐시한다.
 *      스탬프가 캐시와 같으면 재조회 없이 캐시로 노출, 다르면(관리자가 변경) 1회 재조회 후 캐시 갱신.
 *
 * '다시 보지 않기'(로그인을 넘어서는 억제) — 서버 기억(DB).
 *   체크 후 닫으면 /m/ajax/popup/dismiss 로 서버에 저장(회원별). 방식(오늘/영구)은 서버가 판단.
 *   → 앱 재설치·기기 변경에도 유지된다. 서버 조회(listForMember)가 dismiss 된 글을 이미 걸러 내려준다.
 *   → 클라는 닫은 즉시 세션 캐시에서도 그 글을 빼서, 이번 세션 재노출을 막는다.
 *
 * 렌더링 — "남이 쓴 HTML 만 격리한다"
 *   · 일반 공지(NOTICE) : 관리자가 에디터로 쓴 HTML → Shadow DOM 으로 격리(앱 CSS 침범 차단).
 *   · 약관 개정(TERMS)  : 우리가 생성한 마크업 → 격리하지 않고 앱 CSS(.terms-notice__*)로 그린다.
 *   · 이미지만(IMAGE)   : 대표이미지 한 장을 제목 없이 가운데 카드로 (명절 인사 등). 판별은 서버.
 */
const KIND_TERMS = 'TERMS';
const KIND_IMAGE = 'IMAGE';
const CACHE_KEY = 'popupCache';     // sessionStorage — {stamp, popups} 캐시(재조회 방지)

export default class Popup {
    constructor() {
        this._running = false;
    }

    /**
     * 관리자 미리보기 — 글 1건을 실제 팝업 그대로 띄운다 (전용 미리보기 페이지에서 호출).
     * 조회/캐시/스탬프·로그인당 1회 게이트를 거치지 않고, 서버가 넘긴 아이템을 바로 렌더한다.
     * 미리보기이므로 '다시 보지 않기'는 서버에 저장하지 않는다 (_preview).
     * _running 을 선점해, 혹시 겹쳐 도는 start() 가 회원 팝업까지 띄우는 것도 막는다.
     */
    preview = (item) => {
        if (!item) return;
        this._preview = true;
        this._running = true;
        this._show(item, () => {});
    };

    /** 사이트 부트스트랩에서 호출. 매 페이지 로드마다 실행된다. */
    start = () => {
        if (this._running) return;

        // G5 는 /asset/bootstrap 이 만든다 — 없는 문서도 있어 typeof 가드로 읽는다.
        const stamp = (typeof G5 !== 'undefined' && G5.popupStamp) || '';
        if (!stamp) return;                       // 스키마 미반영 등 — 조용히 패스

        this._running = true;

        // 캐시가 현재 스탬프와 같으면 재조회 없이 노출 (매 페이지 쿼리 방지)
        const cached = this._readCache(stamp);
        if (cached) { this._render(cached); return; }
        this._fetch(stamp);
    };

    _fetch = (stamp) => {
        $.ajax({ url: '/m/ajax/popups', method: 'GET', dataType: 'json' })
            .done((res) => {
                if (!res || !res.result) { this._running = false; return; }
                const popups = res.popups || [];
                this._writeCache(res.stamp || stamp, popups);
                this._render(popups);
            })
            .fail(() => { this._running = false; });
    };

    /** 큐 노출. dismiss 필터는 서버(listForMember)가 이미 했으므로 그대로 띄운다. */
    _render = (popups) => {
        this._queue((popups || []).slice());   // 원본 캐시 배열 보존 위해 복제
    };

    /** 큐를 하나씩 순차 노출. */
    _queue = (list) => {
        if (!list.length) { this._running = false; return; }
        const item = list.shift();
        this._show(item, () => this._queue(list));
    };

    // 현재 회원 id. sessionStorage 는 브라우저 탭 단위라, 관리자 대리로그인으로 회원이 바뀌면
    // 캐시가 새어 다른 회원의 팝업 판단을 재사용한다 → 회원 id 를 캐시 키에 포함해 분리한다.
    _mb = () => (typeof MEMBER !== 'undefined' && MEMBER && MEMBER.num) || 0;

    _readCache = (stamp) => {
        const obj = $j.Common.Store.session.getJSON(CACHE_KEY);   // 없음/파싱실패 → null
        return (obj && obj.stamp === stamp && obj.mb === this._mb()) ? obj.popups : null;
    };

    _writeCache = (stamp, popups) => {
        $j.Common.Store.session.setJSON(CACHE_KEY, { stamp, mb: this._mb(), popups }); // Store 가 try/catch 내장
    };

    _show = (item, onClose) => {
        if (item.kind === KIND_IMAGE) { this._showImage(item, onClose); return; }
        const isTerms = item.kind === KIND_TERMS;
        // 대표 이미지가 있으면 본문 상단에 노출. (우리가 넣는 <img> 라 격리 밖에 둔다)
        const coverHtml = item.cover
            ? `<div class="popup-modal__cover"><img src="${item.cover}" alt=""></div>`
            : '';
        const bodyHtml = coverHtml + (isTerms
            ? `<div class="terms-notice-wrap">${item.body || ''}</div>`   // 우리 마크업 — 앱 CSS 그대로
            : '<div class="popup-shadow-host"></div>');                   // 에디터 HTML — Shadow 로 격리

        let $footer = null;   // 닫힘 시점에 '다시 보지 않기' 체크를 읽기 위해 참조 보관

        const modal = $j.Ui.Modal.open({
            mode: 'full',
            variant: isTerms ? 'terms' : 'notice',
            panelClass: 'popup-modal',
            title: item.title || '',
            html: bodyHtml,
            dismissible: false,      // 백드롭·ESC 로는 안 닫히고 X/확인 으로만
            // 어떻게 닫든(X·확인) 체크돼 있으면 '다시 보지 않기'를 서버에 저장한 뒤 큐를 진행한다.
            onClose: () => {
                if ($footer && $footer.find('.js-dismiss').is(':checked')) {
                    this._dismiss(item.num);
                }
                onClose();
            },
        });

        if (!isTerms) {
            this._renderIsolated(modal.el.find('.popup-shadow-host')[0], item.body || '');
        }

        $footer = this._appendFooter(modal, item);
    };

    /** 이미지 한 장짜리 팝업 — 제목·X 없이 이미지 + 하단 푸터. 닫기·억제 규칙은 일반 팝업과 같다. */
    _showImage = (item, onClose) => {
        let $footer = null;
        const modal = $j.Ui.Modal.open({
            mode: 'center',
            variant: 'image',
            panelClass: 'popup-modal popup-modal--image',
            html: `<img class="popup-modal__image" src="${item.cover}" alt="">`,
            dismissible: false,
            onClose: () => {
                if ($footer && $footer.find('.js-dismiss').is(':checked')) this._dismiss(item.num);
                onClose();
            },
        });
        $footer = this._appendFooter(modal, item);
    };

    /** 에디터 HTML 을 Shadow DOM 안에 그린다 — 바깥 앱 CSS 가 침범하지 못한다. */
    _renderIsolated = (host, html) => {
        if (!host) return;
        const root = host.attachShadow ? host.attachShadow({ mode: 'open' }) : null;
        if (!root) { host.innerHTML = html; return; }   // 미지원 환경 폴백

        // 상속 속성(폰트/색/행간)은 shadow 경계를 넘어 들어오므로 :host 에서 끊는다.
        root.innerHTML = `
<style>
  :host { all: initial; display: block; }
  .content { font: 15px/1.7 -apple-system,"Apple SD Gothic Neo","Malgun Gothic",sans-serif; color: #222; word-break: break-word; }
  .content img { max-width: 100%; height: auto; }
  .content table { width: 100%; border-collapse: collapse; }
  .content td, .content th { border: 1px solid #ddd; padding: 6px 8px; }
  .content a { color: #1d4ed8; }
  .content * { max-width: 100%; }
</style>
<div class="content">${html}</div>`;

        // jQuery 선택자는 shadow 경계를 통과하지 못한다 — 링크 가로채기는 root 에 직접 건다.
        root.querySelectorAll('a[href]').forEach((a) => {
            a.addEventListener('click', (e) => {
                e.preventDefault();
                const href = a.getAttribute('href');
                if (href) location.href = href;   // 웹뷰에서 열기
            });
        });
    };

    /** 하단 '다시 보지 않기' + '확인'. 확인은 modal.close() 만 호출 — 실제 dismiss 처리는 onClose 가 한다. */
    _appendFooter = (modal, item) => {
        // 매 페이지 노출 정책이라 '다시 보지 않기'는 항상 표시(유일한 억제 수단). 관리자는 방식만 고른다.
        const dismissLabel = item.dismiss_mode === 'NEVER' ? '다시 보지 않기' : '오늘 하루 보지 않기';

        // 체크 모양은 .popup-modal__check(span)이 그린다 — 네이티브 input 은 숨긴다.
        // 팝업이 뜨는 페이지마다 input[type=checkbox] 전역 규칙(일지 m-diary.css 의 원형 SVG 등)이
        // 달라서, 입력을 그대로 노출하면 체크하는 순간 모양이 깨진다. (components/_popup.scss 참고)
        const $footer = $(`
<div class="popup-modal__footer">
  <label class="popup-modal__dismiss">
    <input type="checkbox" class="js-dismiss">
    <span class="popup-modal__check" aria-hidden="true"></span>
    <span class="popup-modal__dismiss-text">${dismissLabel}</span>
  </label>
  <button type="button" class="popup-modal__confirm js-confirm">확인</button>
</div>`);

        $footer.find('.js-confirm').on('click', () => modal.close());
        modal.el.find('.ui-modal__body').after($footer);
        return $footer;
    };

    /* ── 다시 보지 않기 (서버 기억) ─────────────────────── */

    _dismiss = (id) => {
        if (this._preview) return;   // 미리보기에서는 서버에 저장하지 않는다
        // 서버에 저장 (방식은 서버가 게시글 설정에서 판단).
        $.ajax({ url: '/m/ajax/popup/dismiss', method: 'POST', data: { post_id: id }, dataType: 'json' });
        // 캐시를 '편집'하지 않고 통째로 무효화한다. 다음 페이지에서 서버로부터 새 목록을 받으므로
        //   (서버가 dismiss 된 글을 이미 걸러줌) 빈 목록이 굳는 함정이 없다.
        $j.Common.Store.session.remove(CACHE_KEY);
    };
}
